import { useState } from "react";

/* PACKAGES */
import Rating from "react-rating";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Button,
  Dialog,
  DialogBody,
  Progress,
  Tooltip,
} from "@material-tailwind/react";

/* ICONS */
import { FaStar } from "react-icons/fa";
import { FaRegHeart, FaCircleCheck } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { GoArrowUpRight } from "react-icons/go";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

/* ASSETS */
import Product01 from "../../assets/images/products/product-01.webp";
import Product03 from "../../assets/images/products/product-03.webp";
import Product04 from "../../assets/images/products/product-04.webp";

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    x: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

const ProductBox = (props) => {
  const [quickView, setQuickView] = useState(false);
  const [quantity, setQuantity] = useState(1);

  /* STOCK PROGRESSBAR */
  const StockProgressBar = (props) => {
    return (
      <>
        <p className="text-sm font-source font-normal text-black">
          Hurry, only{" "}
          <span className="font-source font-bold">{props.items}</span> items
          left in stock!
        </p>
        <div className="mt-1">
          <Progress value={props.items} className=" h-2" />
        </div>
      </>
    );
  };

  /* QUANTITY ADD HANDLER */
  const addQuantity = () => {
    setQuantity(quantity + 1);
  };

  /* QUALITY SUBTRACT HANDLER */
  const subtractQuantity = () => {
    if (quantity === 1) {
      setQuantity(1);
    } else {
      setQuantity(quantity - 1);
    }
  };

  /* QUANTITY BOX */
  const QuantityBox = (props) => {
    return (
      <div className="flex items-center">
        <Button
          className="h-11 shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 border border-r-0 border-greyBorder rounded-l-md rounded-r-none py-2 px-3 flex items-center justify-center"
          onClick={subtractQuantity}
        >
          <IoIosArrowBack className="w-4 h-4 text-black" />
        </Button>
        <div className="w-[5vh] h-11">
          <input
            type="number"
            name="quantity"
            id="quantity"
            className="w-full h-full block font-source font-semibold placeholder:font-source placeholder:font-normal text-sm text-black p-0 focus:outline-none border border-greyBorder text-center"
            value={props.value}
            onChange={props.onChange}
          />
        </div>
        <Button
          className="h-11 shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 border border-l-0 border-greyBorder rounded-l-none rounded-r-md py-2 px-3 flex items-center justify-center"
          onClick={addQuantity}
        >
          <IoIosArrowForward className="w-4 h-4 text-black" />
        </Button>
      </div>
    );
  };

  return (
    <>
      <motion.div
        variants={animation}
        className="group w-full h-full bg-white border border-greyBorder shadow-sm p-4 rounded-2xl hover:border-primaryRed hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ease-in-out flex flex-col"
      >
        <Link to="/product-detail">
          <div className="bg-gradient-to-br from-greyBg via-white to-greyBg rounded-2xl py-2 flex items-center justify-center relative border border-greyBorder">
            <img
              src={props.image}
              className="h-56 w-auto object-contain mix-blend-multiply"
              alt={props.name}
            />
          </div>
        </Link>
        <div className="mt-3 flex flex-col flex-1">
          <Link to="/product-detail">
            <h5 className="text-lg font-source font-semibold text-black group-hover:text-primaryRed transition-all duration-300 ease-in-out">
              {props.name}
            </h5>
          </Link>

          <div className="mt-auto pt-3 grid grid-cols-2 gap-3">
            <Button className="w-full bg-primaryRed hover:bg-opacity-80 rounded-full shadow-none hover:shadow-none text-white text-sm font-source font-semibold normal-case px-4 py-2">
              Add to bag
            </Button>
            <Button className="w-full bg-white border border-greyBorder hover:border-red-500 hover:text-red-500 rounded-full shadow-none hover:shadow-none text-sm font-source font-semibold text-black normal-case px-4 py-2">
              Mark as favorite
            </Button>
          </div>
        </div>
      </motion.div>

      {/* QUICK VIEW */}
      <Dialog
        open={quickView}
        handler={() => setQuickView(!quickView)}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
        size="xl"
      >
        <DialogBody className="p-5">
          <motion.div
            variants={animation}
            className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-10 lg:gap-10 items-center"
          >
            {/* PRODUCT IMAGES */}
            <motion.div variants={animation} className="col-span-2 h-full">
              <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-5 lg:gap-5 h-full">
                <div className="col-span-1">
                  <div className="max-h-full overflow-auto overflow-x-hidden overflow-y-auto hide-scrollbar flex xl:block lg:block gap-5 xl:gap-0 lg:gap-0">
                    <div className="w-full h-full rounded-xl bg-productImg border border-greyBorder border-opacity-50 p-2 shadow-inner flex items-center justify-center">
                      <img
                        src={Product03}
                        className="w-full h-[15vh] xl:h-productSmall lg:h-productSmall object-contain rounded-lg mix-blend-multiply"
                        alt="Organic Black Pepper by Harela"
                      />
                    </div>
                    <div className="mt-0 xl:mt-5 lg:mt-5 w-full h-full rounded-xl bg-productImg border border-greyBorder border-opacity-50 p-2 shadow-inner flex items-center justify-center">
                      <img
                        src={Product04}
                        className="w-full h-[15vh] xl:h-productSmall lg:h-productSmall object-contain rounded-lg mix-blend-multiply"
                        alt="Organic Black Pepper by Harela"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="w-full h-full rounded-xl bg-productImg border border-greyBorder border-opacity-50 shadow-inner p-2 flex items-center justify-center">
                    <img
                      src={Product01}
                      className="w-full h-[40vh] xl:h-product lg:h-product object-contain rounded-lg mix-blend-multiply"
                      alt="Organic Black Pepper by Harela"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* PRODUCT DETAILS */}
            <motion.div variants={animation} className="col-span-1 relative">
              {/* HEADING */}
              <div className="flex items-center justify-between mt-2">
                <div className="w-[90%] xl:w-4/5 lg:w-4/5">
                  <h1 className="text-3xl font-fraunces-bold tracking-tight text-black">
                    Organic Black Pepper
                  </h1>
                  <h2 className="text-lg font-source font-normal text-headerGrey mt-1">
                    by Harela
                  </h2>
                </div>
                <div className="w-[10%] xl:w-1/5 lg:w-1/5 flex items-center justify-end">
                  <Button className="shadow-none hover:shadow-none border border-greyBorder rounded-full p-3 bg-transparent hover:bg-black hover:bg-opacity-5 transition-all duration-300 ease-in-out">
                    <FaRegHeart className="w-5 h-5 text-black" />
                  </Button>
                </div>
              </div>

              {/* REVIEWS AND PRICING */}
              <div className="mt-5">
                {/* REVIEWS */}
                <a href="/" className="w-fit block group">
                  <div className="flex items-center gap-2">
                    <Rating
                      readonly
                      initialRating={4}
                      fullSymbol={<FaStar className="w-5 h-5 text-[#FACC15]" />}
                      emptySymbol={
                        <FaStar className="w-5 h-5 text-[#D1D5D8]" />
                      }
                      className="!flex gap-[0.2vh]"
                    />
                    <p className="text-base font-source font-normal text-headerGrey group-hover:text-primaryRed leading-none transition-all duration-300 ease-in-out">
                      10 reviews
                    </p>
                  </div>
                </a>

                {/* PRICING */}
                <div className="flex items-end gap-2 mt-8">
                  <p className="text-2xl font-source font-bold text-logoGreen leading-none">
                    ₹1,151.00
                  </p>
                  <p className="text-base font-source font-semibold text-headerGrey line-through">
                    ₹2500.00
                  </p>
                </div>

                {/* STOCK RANGE */}
                <div className="mt-8">
                  <StockProgressBar items={50} />
                </div>
              </div>

              {/* CART BTN AND QUANTITY */}
              <div className="flex items-center gap-5 mt-8">
                <QuantityBox
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                <Button className="w-full h-11 shadow-none hover:shadow-none bg-black hover:bg-opacity-80 text-base font-source font-semibold text-white py-2 rounded-md normal-case">
                  Add to cart
                </Button>
              </div>

              {/* BUY BTN */}
              <Link to="/product-detail">
                <Button className="w-full py-2 bg-white hover:bg-black hover:bg-opacity-5 shadow-none hover:shadow-none text-black mt-5 text-base font-source font-semibold border-2 border-black rounded-md normal-case transition-all duration-300 ease-in-out">
                  View product details
                </Button>
              </Link>

              {/* STORE AVAILAIBILITY */}
              <div className="mt-5">
                <div className="w-full bg-greyBg rounded-md p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                      <FaCircleCheck className="w-5 h-5 text-logoGreen" />
                      <div>
                        <h5 className="text-base font-source font-semibold text-black leading-none">
                          Pickup available at our store
                        </h5>
                        <p className="text-sm font-source font-normal text-headerGrey leading-none mt-2">
                          Usually ready in 24 hours
                        </p>
                      </div>
                    </div>
                    <a
                      href="https://maps.app.goo.gl/HsG67vQ1gmgiAvst6"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Tooltip
                        content="Get directions"
                        className="font-source font-semibold"
                      >
                        <Button className="bg-transparent shadow-none hover:shadow-none p-2 rounded-full border border-headerGrey text-headerGrey hover:bg-black hover:text-white transition-all duration-300 ease-in-out">
                          <GoArrowUpRight className="w-5 h-5" />
                        </Button>
                      </Tooltip>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* CLOSE BUTTON */}
          <div className="absolute top-1 right-1">
            <div className="flex items-center justify-end">
              <Button
                className="p-2 rounded-full bg-white hover:bg-black hover:bg-opacity-10 transition-all duration-300 ease-in-out shadow-none hover:shadow-none"
                onClick={() => setQuickView(!quickView)}
              >
                <IoClose className="w-4 h-4 text-black" />
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};

export default ProductBox;
